
import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { FaEdit, FaTrash } from 'react-icons/fa';
import config from "../../../config/config";
import { getAuthToken } from "../../../utils/token";
import axios from "axios";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../../loader/Loader';
import Select from "react-select";

export default function Die() {

  const [dataList, setDataList] = useState([]);
  const [filterText, setFilterText] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [listLoading, setListLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const [machineList, setMachineList] = useState([]);

  const initialState = {
    name: '',
    code: '',
    type: '',
    machine_id: '',
    usage_count: '',
    max_cycles: '',
    max_cycles_per_day: '',
    max_cycles_per_month: '',
    max_cycles_per_year: '',
    purchase_date: '',
    last_service_date: '',
    next_service_date: '',
    expiry_date: ''
  };

  const [form, setForm] = useState(initialState);

  // =========================================================
  // LOAD DATA
  // =========================================================

  useEffect(() => {
    fetchList();
    fetchMachineList();
  }, []);

  // =========================================================
  // FETCH DIE LIST
  // =========================================================

  const fetchList = async () => {

    const token = getAuthToken();

    if (!token) return;

    setListLoading(true);

    try {

      const res = await axios.get(
        `${config.API}/admin/lisdie`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setDataList(res.data.dies || []);

    } catch (err) {

      console.error(
        "Error fetching die list:",
        err
      );

      toast.error("Failed to load die data");

    } finally {

      setListLoading(false);
    }
  };

  // =========================================================
  // FETCH MACHINE LIST
  // =========================================================

  const fetchMachineList = async () => {

    const token = getAuthToken();

    if (!token) return;

    try {

      const res = await axios.get(
        `${config.API}/machine/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setMachineList(
        res.data.machines || []
      );

    } catch (err) {

      console.error(
        "fetchMachineList:",
        err
      );

      toast.error(
        "Failed to load machines"
      );
    }
  };

  // =========================================================
  // INPUT HANDLER
  // =========================================================

  const handleInput = (e) => {

    const {
      name,
      value
    } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: value
    }));

    setErrors(prev => ({
      ...prev,
      [name]: null
    }));
  };

  // =========================================================
  // OPEN ADD MODAL
  // =========================================================

  const openAddModal = () => {

    setForm({
      ...initialState
    });

    setErrors({});

    setIsEditing(false);

    setEditingId(null);

    setShowModal(true);
  };

  // =========================================================
  // OPEN EDIT MODAL
  // =========================================================

  const openEditModal = (item) => {

    setForm({
      ...initialState,
      ...item,

      machine_id:
        item.machine_id ??
        item.machine?.id ??
        '',

      purchase_date:
        item.purchase_date
          ? String(item.purchase_date).substring(0, 10)
          : '',

      last_service_date:
        item.last_service_date
          ? String(item.last_service_date).substring(0, 10)
          : '',

      next_service_date:
        item.next_service_date
          ? String(item.next_service_date).substring(0, 10)
          : '',

      expiry_date:
        item.expiry_date
          ? String(item.expiry_date).substring(0, 10)
          : ''
    });

    setErrors({});

    setIsEditing(true);

    setEditingId(item.id);

    setShowModal(true);
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = async (id) => {

    if (
      !window.confirm(
        "Are you sure you want to delete this die?"
      )
    ) {
      return;
    }

    const token = getAuthToken();

    if (!token) {
      toast.error("Authentication missing");
      return;
    }

    try {

      /*
       * Change this URL if your Laravel delete
       * route is different.
       */
      await axios.delete(
        `${config.API}/admin/deletedie/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success(
        "Deleted successfully"
      );

      fetchList();

    } catch (err) {

      console.error(
        "Delete error:",
        err
      );

      toast.error(
        err.response?.data?.message ||
        "Failed to delete die"
      );
    }
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async () => {

    const token = getAuthToken();

    if (!token) {

      toast.error(
        "Authentication missing"
      );

      return;
    }

    setErrors({});

    const payload = {

      name: form.name,

      code: form.code,

      type: form.type,

      machine_id:
        form.machine_id
          ? Number(form.machine_id)
          : null,

      usage_count:
        form.usage_count !== ''
          ? Number(form.usage_count)
          : 0,

      max_cycles:
        form.max_cycles !== ''
          ? Number(form.max_cycles)
          : 0,

      max_cycles_per_day:
        form.max_cycles_per_day !== ''
          ? Number(form.max_cycles_per_day)
          : 0,

      max_cycles_per_month:
        form.max_cycles_per_month !== ''
          ? Number(form.max_cycles_per_month)
          : 0,

      max_cycles_per_year:
        form.max_cycles_per_year !== ''
          ? Number(form.max_cycles_per_year)
          : 0,

      purchase_date:
        form.purchase_date || null,

      last_service_date:
        form.last_service_date || null,

      next_service_date:
        form.next_service_date || null,

      expiry_date:
        form.expiry_date || null
    };

    setFormLoading(true);

    try {

      if (
        isEditing &&
        editingId
      ) {

        await axios.put(
          `${config.API}/admin/updatedie/${editingId}`,
          payload,
          {
            headers: {
              Authorization:
                `Bearer ${token}`
            }
          }
        );

        toast.success(
          "Updated successfully"
        );

      } else {

        await axios.post(
          `${config.API}/admin/adddie`,
          payload,
          {
            headers: {
              Authorization:
                `Bearer ${token}`
            }
          }
        );

        toast.success(
          "Created successfully"
        );
      }

      setShowModal(false);

      fetchList();

    } catch (err) {

      if (
        err.response?.status === 422
      ) {

        const data =
          err.response.data;

        if (data.errors) {

          setErrors(
            data.errors
          );

        } else if (data.message) {

          toast.error(
            data.message
          );
        }

      } else {

        console.error(
          "Submit error:",
          err
        );

        toast.error(
          "Submit failed! Please try again."
        );
      }

    } finally {

      setFormLoading(false);
    }
  };

  // =========================================================
  // VALIDATION ERROR
  // =========================================================

  const renderError = (field) => {

    return errors[field]
      ? (
        <div className="text-danger small">
          {errors[field][0]}
        </div>
      )
      : null;
  };

  // =========================================================
  // SEARCH
  // =========================================================

  const filteredData = dataList.filter(
    (item) => {

      const searchText = [

        item.id,

        item.name,

        item.code,

        item.type,

        item.machine?.name,

        item.machine?.machine_code,

        item.usage_count,

        item.max_cycles,

        item.max_cycles_per_day,

        item.max_cycles_per_month,

        item.max_cycles_per_year,

        item.purchase_date,

        item.last_service_date,

        item.next_service_date,

        item.expiry_date

      ]
        .filter(
          value =>
            value !== null &&
            value !== undefined
        )
        .join(' ')
        .toLowerCase();

      return searchText.includes(
        filterText.toLowerCase()
      );
    }
  );

  // =========================================================
  // MACHINE OPTIONS
  // =========================================================

  const machineOptions =
    machineList.map((machine) => ({
      value: machine.id,
      label:
        machine.machine_code ||
        machine.name
    }));

  // =========================================================
  // DATA GRID COLUMNS
  // =========================================================

  const columns = [

    {
      field: 'id',
      headerName: 'ID',
      width: 80
    },

    {
      field: 'name',
      headerName: 'Name',
      minWidth: 180,
      flex: 1
    },

    {
      field: 'code',
      headerName: 'Code',
      minWidth: 130,
      width: 120
    },

    {
      field: 'type',
      headerName: 'Type',
      minWidth: 120,
      width: 100
    },

    {
      field: 'machine_name',
      headerName: 'Machine',
      minWidth: 120,
      flex: 1,

      valueGetter: (
        value,
        row
      ) =>
        row.machine?.name ||
        row.machine?.machine_code ||
        ''
    },

    {
      field: 'usage_count',
      headerName: 'Usage Count',
      width: 100,
      type: 'number'
    },

    {
      field: 'max_cycles',
      headerName: 'Max Cycles',
      width: 100,
      type: 'number'
    },

    {
      field: 'max_cycles_per_day',
      headerName: 'Max/Day',
      width: 100,
      type: 'number'
    },

    /*
     * Uncomment if you want these columns displayed.
     */

    // {
    //   field: 'max_cycles_per_month',
    //   headerName: 'Max/Month',
    //   width: 120,
    //   type: 'number'
    // },

    // {
    //   field: 'max_cycles_per_year',
    //   headerName: 'Max/Year',
    //   width: 120,
    //   type: 'number'
    // },

    // {
    //   field: 'purchase_date',
    //   headerName: 'Purchase Date',
    //   width: 130
    // },

    // {
    //   field: 'last_service_date',
    //   headerName: 'Last Service',
    //   width: 130
    // },

    // {
    //   field: 'next_service_date',
    //   headerName: 'Next Service',
    //   width: 130
    // },

    // {
    //   field: 'expiry_date',
    //   headerName: 'Expiry Date',
    //   width: 130
    // },

    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,

      renderCell: (params) => (

        <div
          className="d-flex gap-2"
          style={{
            height: '100%',
            alignItems: 'center'
          }}
        >

          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() =>
              openEditModal(
                params.row
              )
            }
            title="Edit"
          >
            <FaEdit />
          </button>

          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() =>
              handleDelete(
                params.row.id
              )
            }
            title="Delete"
          >
            <FaTrash />
          </button>

        </div>
      )
    }
  ];

  // =========================================================
  // RENDER
  // =========================================================

  return (

    <div className="container-fluid mt-3">

      {/* =====================================================
          HEADER
      ====================================================== */}

      <div className="d-flex justify-content-between align-items-center mb-3">

        <strong className="mb-0 fs-4">
          Die Details
        </strong>

        <div className="d-flex gap-2">

          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            value={filterText}
            onChange={(e) =>
              setFilterText(
                e.target.value
              )
            }
            style={{
              width: '250px'
            }}
          />

          <button
            className="btn btn-primary"
            onClick={openAddModal}
          >
            + Add Die
          </button>

        </div>

      </div>

      {/* =====================================================
          DATA GRID
      ====================================================== */}

      {listLoading ? (

        <div className="text-center py-5">
          <Loader />
        </div>

      ) : (

        <div
          style={{
            width: '100%',
            backgroundColor: '#fff'
          }}
        >

          <DataGrid

            rows={filteredData}

            columns={columns}

            getRowId={(row) =>
              row.id
            }

            autoHeight

            pagination

            pageSizeOptions={[
              10,
              25,
              50,
              100
            ]}

            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                  page: 0
                }
              }
            }}

            disableRowSelectionOnClick

            sx={{

              width: '100%',

              border:
                '1px solid #dee2e6',

              /* ==========================
                 COLUMN HEADER
              ========================== */

              '& .MuiDataGrid-columnHeaders': {
                backgroundColor:
                  '#0d6efd !important',

                color:
                  '#ffffff !important',

                minHeight:
                  '52px !important',

                maxHeight:
                  '52px !important'
              },

              '& .MuiDataGrid-columnHeader': {
                backgroundColor:
                  '#0d6efd !important',

                color:
                  '#ffffff !important',

                minHeight:
                  '52px !important',

                maxHeight:
                  '52px !important'
              },

              /* ==========================
                 COLUMN HEADER LABEL
              ========================== */

              '& .MuiDataGrid-columnHeaderTitle': {
                color:
                  '#ffffff !important',

                fontWeight:
                  '700 !important',

                fontSize:
                  '14px',

                whiteSpace:
                  'normal'
              },

              /* ==========================
                 SORT ICON
              ========================== */

              '& .MuiDataGrid-sortIcon': {
                color:
                  '#ffffff !important'
              },

              '& .MuiDataGrid-iconButtonContainer': {
                color:
                  '#ffffff !important'
              },

              /* ==========================
                 MENU ICON
              ========================== */

              '& .MuiDataGrid-menuIconButton': {
                color:
                  '#ffffff !important'
              },

              /* ==========================
                 ROW
              ========================== */

              '& .MuiDataGrid-row:hover': {
                backgroundColor:
                  '#f5f9ff'
              },

              /* ==========================
                 CELL
              ========================== */

              '& .MuiDataGrid-cell': {
                fontSize:
                  '14px'
              },

              /* ==========================
                 FOOTER
              ========================== */

              '& .MuiDataGrid-footerContainer': {
                borderTop:
                  '1px solid #dee2e6'
              }
            }}

          />

        </div>
      )}

      {/* =====================================================
          MODAL
      ====================================================== */}

      {showModal && (

        <div
          className="modal d-block"
          style={{
            background:
              'rgba(0,0,0,0.5)'
          }}
        >

          <div className="modal-dialog modal-lg">

            <div className="modal-content">

              {/* HEADER */}

              <div className="modal-header bg-primary text-light">

                <h5 className="mb-0">

                  {isEditing
                    ? 'Edit Die'
                    : 'Add Die'}

                </h5>

                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() =>
                    setShowModal(false)
                  }
                  disabled={
                    formLoading
                  }
                />

              </div>

              {/* BODY */}

              <div className="modal-body">

                <div className="row g-3">

                  {/* NAME */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Name
                    </label>

                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      value={
                        form.name
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "name"
                    )}

                  </div>

                  {/* CODE */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Code
                    </label>

                    <input
                      type="text"
                      name="code"
                      className="form-control"
                      value={
                        form.code
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "code"
                    )}

                  </div>

                  {/* TYPE */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Type
                    </label>

                    <input
                      type="text"
                      name="type"
                      className="form-control"
                      value={
                        form.type
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "type"
                    )}

                  </div>

                  {/* MACHINE */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Machine
                    </label>

                    <Select

                      name="machine_id"

                      value={
                        machineOptions.find(
                          option =>
                            option.value ===
                            form.machine_id
                        ) || null
                      }

                      onChange={
                        (selectedOption) => {

                          setForm(prev => ({
                            ...prev,

                            machine_id:
                              selectedOption
                                ? selectedOption.value
                                : ""
                          }));

                          setErrors(prev => ({
                            ...prev,
                            machine_id:
                              null
                          }));
                        }
                      }

                      options={
                        machineOptions
                      }

                      isDisabled={
                        formLoading
                      }

                      placeholder="-- Select Machine --"

                      isSearchable

                      styles={{
                        control:
                          (base) => ({
                            ...base,

                            borderColor:
                              errors.machine_id
                                ? "red"
                                : base.borderColor
                          })
                      }}

                    />

                    {renderError(
                      "machine_id"
                    )}

                  </div>

                  {/* USAGE COUNT */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Usage Count
                    </label>

                    <input
                      type="number"
                      name="usage_count"
                      className="form-control"
                      value={
                        form.usage_count
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "usage_count"
                    )}

                  </div>

                  {/* MAX CYCLES */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Max Cycles
                    </label>

                    <input
                      type="number"
                      name="max_cycles"
                      className="form-control"
                      value={
                        form.max_cycles
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "max_cycles"
                    )}

                  </div>

                  {/* MAX PER DAY */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Max Cycles Per Day
                    </label>

                    <input
                      type="number"
                      name="max_cycles_per_day"
                      className="form-control"
                      value={
                        form.max_cycles_per_day
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "max_cycles_per_day"
                    )}

                  </div>

                  {/* MAX PER MONTH */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Max Cycles Per Month
                    </label>

                    <input
                      type="number"
                      name="max_cycles_per_month"
                      className="form-control"
                      value={
                        form.max_cycles_per_month
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "max_cycles_per_month"
                    )}

                  </div>

                  {/* MAX PER YEAR */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Max Cycles Per Year
                    </label>

                    <input
                      type="number"
                      name="max_cycles_per_year"
                      className="form-control"
                      value={
                        form.max_cycles_per_year
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "max_cycles_per_year"
                    )}

                  </div>

                  {/* PURCHASE DATE */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Purchase Date
                    </label>

                    <input
                      type="date"
                      name="purchase_date"
                      className="form-control"
                      value={
                        form.purchase_date
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "purchase_date"
                    )}

                  </div>

                  {/* LAST SERVICE */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Last Service Date
                    </label>

                    <input
                      type="date"
                      name="last_service_date"
                      className="form-control"
                      value={
                        form.last_service_date
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "last_service_date"
                    )}

                  </div>

                  {/* NEXT SERVICE */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Next Service Date
                    </label>

                    <input
                      type="date"
                      name="next_service_date"
                      className="form-control"
                      value={
                        form.next_service_date
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "next_service_date"
                    )}

                  </div>

                  {/* EXPIRY */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Expiry Date
                    </label>

                    <input
                      type="date"
                      name="expiry_date"
                      className="form-control"
                      value={
                        form.expiry_date
                      }
                      onChange={
                        handleInput
                      }
                      disabled={
                        formLoading
                      }
                    />

                    {renderError(
                      "expiry_date"
                    )}

                  </div>

                </div>

              </div>

              {/* FOOTER */}

              <div className="modal-footer">

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    setShowModal(false)
                  }
                  disabled={
                    formLoading
                  }
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={
                    handleSubmit
                  }
                  disabled={
                    formLoading
                  }
                >

                  {formLoading
                    ? 'Saving...'
                    : isEditing
                      ? 'Update'
                      : 'Save'}

                </button>

              </div>

            </div>

          </div>

        </div>
      )}

    </div>
  );
}
